import axios from 'axios';
import {
  Prescription,
  Patient,
  Medication,
  Prescriber,
  AssessmentTemplate,
  ClinicalAssessment,
  ProcessingMetrics,
  TimeTracking,
  PrescriptionStatus,
  TemplateType,
  QueuePriority,
  ClinicalData,
  PharmacyDirectoryEntry,
} from '../types/pharmacy';
import { API_ROOT } from './serviceConfig';

const API_BASE_URL = API_ROOT;
// All pharmacy endpoints are under /api/v1/pharmacy
const PHARMACY_BASE = '/api/v1/pharmacy';

class PharmacyApiService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Patient Management
  async searchPatients(query: string) {
    const response = await this.api.get(`${PHARMACY_BASE}/patients/search`, { params: { q: query } });
    return response.data;
  }

  async getPatientById(id: number) {
    const response = await this.api.get(`${PHARMACY_BASE}/patients/${id}`);
    return response.data;
  }

  async createPatient(patient: Partial<Patient>) {
    const response = await this.api.post(`${PHARMACY_BASE}/patients`, patient);
    return response.data;
  }

  // Prescription Processing
  async getPrescriptions(params: {
    skip?: number;
    limit?: number;
    status?: PrescriptionStatus;
    priority?: QueuePriority;
    patient_id?: number;
  } = {}) {
    const response = await this.api.get(`${PHARMACY_BASE}/prescriptions`, { params });
    return response.data;
  }

  async getPrescriptionById(id: number) {
    const response = await this.api.get(`${PHARMACY_BASE}/prescriptions/${id}`);
    return response.data;
  }

  async createPrescription(prescription: Partial<Prescription>) {
    const response = await this.api.post(`${PHARMACY_BASE}/prescriptions`, prescription);
    return response.data;
  }

  async updatePrescription(id: number, updates: Partial<Prescription>) {
    const response = await this.api.put(`${PHARMACY_BASE}/prescriptions/${id}`, updates);
    return response.data;
  }

  async processPrescription(id: number, notes?: string) {
    const response = await this.api.post(`${PHARMACY_BASE}/prescriptions/${id}/process`, null, {
      params: notes ? { notes } : {}
    });
    return response.data;
  }

  async completePrescription(id: number) {
    const response = await this.api.post(`${PHARMACY_BASE}/prescriptions/${id}/complete`);
    return response.data;
  }

  async cancelPrescription(id: number, reason: string) {
    const response = await this.api.post(`${PHARMACY_BASE}/prescriptions/${id}/cancel`, null, {
      params: { reason }
    });
    return response.data;
  }

  // Medication Management
  async searchMedications(query: string) {
    const response = await this.api.get(`${PHARMACY_BASE}/medications/search`, { params: { q: query } });
    return response.data;
  }

  async getMedicationById(id: number) {
    const response = await this.api.get(`${PHARMACY_BASE}/medications/${id}`);
    return response.data;
  }

  async getLowInventory() {
    const response = await this.api.get(`${PHARMACY_BASE}/medications/low-inventory`);
    return response.data;
  }

  // Assessment Templates
  async getAssessmentTemplates(type?: TemplateType) {
    const params = type ? { assessment_type: type } : {};
    const response = await this.api.get(`${PHARMACY_BASE}/assessment-templates`, { params });
    return response.data;
  }

  async createAssessmentTemplate(template: Partial<AssessmentTemplate>) {
    const response = await this.api.post(`${PHARMACY_BASE}/assessment-templates`, template);
    return response.data;
  }

  async saveClinicalAssessment(assessment: Partial<ClinicalAssessment>) {
    const response = await this.api.post(`${PHARMACY_BASE}/clinical-assessments`, assessment);
    return response.data;
  }

  async getClinicalAssessments(prescriptionId: number) {
    const response = await this.api.get(`${PHARMACY_BASE}/prescriptions/${prescriptionId}/assessments`);
    return response.data;
  }

  // Document Generation
  async generateDocument(prescriptionId: number, documentType: string, options?: any) {
    const response = await this.api.post(`${PHARMACY_BASE}/prescriptions/${prescriptionId}/documents`, {
      document_type: documentType,
      ...options
    });
    return response.data;
  }

  // Metrics
  async getMetrics() {
    const response = await this.api.get(`${PHARMACY_BASE}/metrics`);
    return response.data;
  }

  // Daily stats for dashboard
  async getDailyStats() {
    const response = await this.api.get(`${PHARMACY_BASE}/stats/daily`);
    return response.data;
  }

  // Today's safety alerts
  async getTodayAlerts() {
    const response = await this.api.get(`${PHARMACY_BASE}/alerts/today`);
    return response.data;
  }

  // Time Tracking
  async logTimeTracking(data: Partial<TimeTracking>) {
    const response = await this.api.post(`${PHARMACY_BASE}/time-tracking`, data);
    return response.data;
  }

  // DAP Wizard — medication clinical data (DUR rules, checks, counselling)
  async getMedicationClinicalData(medicationId: number): Promise<ClinicalData> {
    const response = await this.api.get(`${PHARMACY_BASE}/medications/${medicationId}/clinical-data`);
    return response.data;
  }

  // Pharmacy Directory — search Alberta pharmacies by name, city, or address
  async searchPharmacies(query: string, limit = 20): Promise<PharmacyDirectoryEntry[]> {
    const response = await this.api.get(`${PHARMACY_BASE}/pharmacies/search`, {
      params: { q: query, limit },
    });
    return response.data;
  }
}

export const pharmacyApi = new PharmacyApiService();
export default pharmacyApi;
